import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../../data/learningLevels.json';
import { getDepartmentTranslation } from '../../../services/departmentsTranslationsService';
// Icons
import { 
  PlusIcon, 
  SearchIcon, 
  EyeIcon, 
  EditIcon, 
  TrashIcon,
  XCircleIcon,
  UserCheckIcon,
  UserXIcon,
  BanIcon,
  UnlockIcon,
  KeyIcon,
  SettingsIcon,
  ShieldIcon,
  GraduationCapIcon,
  MailIcon,
  PhoneIcon,
  BarChart3Icon
} from 'lucide-react';

const Students = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function to translate level name
  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  // Fetch learning levels
  const fetchLearningLevels = async () => {
    setLearningLevelsLoading(true);
    try {
      const response = await api.get('/organization/learning-levels?page=1&limit=100');
      if (response.data.success) {
        const levels = response.data.data.learningLevels.map(level => ({
          ...level,
          translatedName: learningLevelsData[level.name] ? 
            learningLevelsData[level.name][currentLanguage] || level.name : 
            level.name
        }));
        setLearningLevels(levels);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
      toast.error(t('users.students.learningLevelsFetchError'));
    } finally {
      setLearningLevelsLoading(false);
    }
  };

  // State
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    isActive: '',
    sortBy: 'first_name',
    sortOrder: 'asc',
    learningLevelId: '',
    departmentId: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [currentLevelInfo, setCurrentLevelInfo] = useState(null);
  const [filterDepartments, setFilterDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Bulk level update state
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showBulkLevelModal, setShowBulkLevelModal] = useState(false);
  const [bulkLevelFormData, setBulkLevelFormData] = useState({
    newLevelId: '',
    graduationDate: ''
  });
  const [bulkLevelFormErrors, setBulkLevelFormErrors] = useState({});
  const [isUpdatingBulkLevel, setIsUpdatingBulkLevel] = useState(false);
  
  // Edit student modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormErrors, setEditFormErrors] = useState({});

  // Update student level modal state
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [updatingStudentLevel, setUpdatingStudentLevel] = useState(null);
  const [isUpdatingLevel, setIsUpdatingLevel] = useState(false);
  const [levelFormErrors, setLevelFormErrors] = useState({});
  const [levelFormData, setLevelFormData] = useState({
    newLevelId: '',
    graduationDate: ''
  });

  // Learning levels state
  const [learningLevels, setLearningLevels] = useState([]);
  const [learningLevelsLoading, setLearningLevelsLoading] = useState(false);

  // Fetch departments for filter
  const fetchDepartmentsForFilter = async () => {
    setDepartmentsLoading(true);
    try {
      let allDepartments = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await api.get(`/organization/departments?page=${currentPage}&limit=100`);
        if (response.data?.success) {
          const fetched = response.data.data?.departments || [];
          allDepartments = [...allDepartments, ...fetched];
          const pagination = response.data.data?.pagination;
          if (pagination && currentPage < pagination.pages) {
            currentPage += 1;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setFilterDepartments(allDepartments);
    } catch (error) {
      console.error('Error fetching departments for filter:', error);
      toast.error(t('users.students.departmentsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Fetch students
  const fetchStudents = async (page = 1, search = '', filtersParam = {}, itemsPerPage = limit) => {
    setLoading(true);
    try {
      // Base params used for normal /students (بدون فلاتر خاصة)
      const baseParams = new URLSearchParams();
      baseParams.set('page', page);
      baseParams.set('limit', itemsPerPage);
      if (search.trim()) baseParams.set('search', search.trim());
      if (filtersParam.isActive === 'true') baseParams.set('isActive', 'true');
      if (filtersParam.isActive === 'false') baseParams.set('isActive', 'false');
      baseParams.set('sortBy', filtersParam.sortBy || 'first_name');
      baseParams.set('sortOrder', filtersParam.sortOrder || 'asc');

      let url = `/students?${baseParams.toString()}`;

      if (filtersParam.learningLevelId) {
        // For level filter, follow backend signature strictly:
        // /students/level/{learning_level_id}?page=1&limit=10
        const levelParams = new URLSearchParams();
        levelParams.set('page', page);
        levelParams.set('limit', itemsPerPage);
        if (search.trim()) levelParams.set('search', search.trim());
        if (filtersParam.isActive === 'true') levelParams.set('isActive', 'true');
        if (filtersParam.isActive === 'false') levelParams.set('isActive', 'false');
        url = `/students/level/${filtersParam.learningLevelId}?${levelParams.toString()}`;
      } else if (filtersParam.departmentId) {
        // Department filter endpoint: /students/organization/filter?page=1&limit=10&departmentId=...
        const departmentParams = new URLSearchParams();
        departmentParams.set('page', page);
        departmentParams.set('limit', itemsPerPage);
        departmentParams.set('departmentId', filtersParam.departmentId);
        if (search.trim()) departmentParams.set('search', search.trim());
        if (filtersParam.isActive === 'true') departmentParams.set('isActive', 'true');
        if (filtersParam.isActive === 'false') departmentParams.set('isActive', 'false');
        url = `/students/organization/filter?${departmentParams.toString()}`;
      }

      const response = await api.get(url);
      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // When filtering by learning level, backend returns { level, students, pagination }
        if (filtersParam.learningLevelId && data.level) {
          const levelStudents = (data.students || []).map((s) => ({
            ...s,
            level_name: s.level_name || data.level.name
          }));
          setStudents(levelStudents);
          setTotalPages(data.pagination?.pages || 1);
          setCurrentPage(data.pagination?.page || page);
          setTotalItems(data.pagination?.total || 0);
          setCurrentLevelInfo(data.level);
        } else {
          // Default / status / department filters: { students, pagination, ... }
          setStudents(data.students || []);
          setTotalPages(data.pagination?.pages || 1);
          setCurrentPage(data.pagination?.page || page);
          setTotalItems(data.pagination?.total || 0);
          setCurrentLevelInfo(null);
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(t('users.students.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTerm === '') {
      fetchStudents(1, '', appliedFilters);
    } else {
      const timeoutId = setTimeout(() => {
        setIsSearching(true);
        fetchStudents(1, searchTerm, appliedFilters);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  // Refetch when limit changes
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    fetchStudents(1, searchTerm, appliedFilters, limit);
  }, [limit]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    fetchStudents(1, searchTerm, appliedFilters);
  };

  // Apply filters
  const applyFilters = () => {
    const newFilters = { ...filters };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchStudents(1, searchTerm, newFilters);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      isActive: '',
      sortBy: 'first_name',
      sortOrder: 'asc',
      learningLevelId: '',
      departmentId: ''
    });
    setAppliedFilters({
      isActive: '',
      sortBy: 'first_name',
      sortOrder: 'asc',
      learningLevelId: '',
      departmentId: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    fetchStudents(1, '', { isActive: '', sortBy: 'first_name', sortOrder: 'asc' });
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (appliedFilters.isActive !== '') count++;
    if (appliedFilters.learningLevelId) count++;
    if (appliedFilters.departmentId) count++;
    return count;
  };

  // Selection helpers
  const toggleSelectStudent = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleSelectAllStudents = () => {
    if (students.length === 0) return;
    if (selectedStudentIds.length === students.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(students.map((s) => s.id));
    }
  };

  const openBulkLevelModal = () => {
    if (!selectedStudentIds.length) {
      toast.error(t('users.students.bulkLevelNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }
    setBulkLevelFormData({
      newLevelId: '',
      graduationDate: ''
    });
    setBulkLevelFormErrors({});
    setShowBulkLevelModal(true);
    fetchLearningLevels();
  };

  const closeBulkLevelModal = () => {
    setShowBulkLevelModal(false);
    setBulkLevelFormData({
      newLevelId: '',
      graduationDate: ''
    });
    setBulkLevelFormErrors({});
  };

  const updateStudentsLevelBulk = async () => {
    if (!selectedStudentIds.length) {
      toast.error(t('users.students.bulkLevelNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }

    const errors = {};
    if (!bulkLevelFormData.newLevelId) {
      errors.newLevelId = t('users.students.newLevelRequired');
    }
    if (!bulkLevelFormData.graduationDate?.trim()) {
      errors.graduationDate = t('users.students.graduationDateRequired');
    }
    setBulkLevelFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsUpdatingBulkLevel(true);
    let successCount = 0;

    try {
      const requestData = {
        newLevelId: parseInt(bulkLevelFormData.newLevelId, 10),
        graduationDate: bulkLevelFormData.graduationDate
      };

      for (const studentId of selectedStudentIds) {
        try {
          const response = await api.put(`/students/${studentId}/level`, requestData);
          if (response.data?.success) {
            successCount += 1;
          }
        } catch (error) {
          console.error('Error updating student level (bulk):', error);
        }
      }

      if (successCount > 0) {
        toast.success(t('users.students.bulkLevelUpdateSuccess', { count: successCount }), {
          duration: 3000,
          position: 'top-center'
        });
        fetchStudents(currentPage, searchTerm, appliedFilters);
        setSelectedStudentIds([]);
        closeBulkLevelModal();
      } else {
        toast.error(t('users.students.bulkLevelUpdateError'), {
          duration: 3000,
          position: 'top-center'
        });
      }
    } finally {
      setIsUpdatingBulkLevel(false);
    }
  };

  // Pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchStudents(page, searchTerm, appliedFilters);
  };

  // View student profile
  const viewStudentProfile = (studentId) => {
    navigate(`/dashboard/students/${studentId}`);
  };

  // Open edit modal
  const openEditModal = (student) => {
    setEditingStudent({
      id: student.id,
      studentNumber: student.student_number,
      enrollmentDate: student.enrollment_date ? student.enrollment_date.split('T')[0] : ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingStudent(null);
    setEditFormErrors({});
  };

  // Open level update modal
  const openLevelModal = (student) => {
    setUpdatingStudentLevel(student);
    setLevelFormData({
      newLevelId: student.current_level_id || '',
      graduationDate: ''
    });
    setLevelFormErrors({});
    setShowLevelModal(true);
    // Fetch learning levels when opening modal
    fetchLearningLevels();
  };

  // Close level update modal
  const closeLevelModal = () => {
    setShowLevelModal(false);
    setUpdatingStudentLevel(null);
    setLevelFormData({
      newLevelId: '',
      graduationDate: ''
    });
    setLevelFormErrors({});
  };

  // Update student
  const updateStudent = async () => {
    if (!editingStudent) return;

    // Validation
    const errors = {};
    if (!editingStudent.studentNumber?.trim()) {
      errors.studentNumber = t('users.students.studentNumberRequired');
    }
    if (!editingStudent.enrollmentDate?.trim()) {
      errors.enrollmentDate = t('users.students.enrollmentDateRequired');
    }

    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsUpdating(true);
    try {
      const requestData = {
        studentNumber: editingStudent.studentNumber.trim(),
        enrollmentDate: editingStudent.enrollmentDate
      };

      console.log('Update Student Request Data:', requestData);
      console.log('API Endpoint: /students/' + editingStudent.id);

      const response = await api.put(`/students/${editingStudent.id}`, requestData);

      if (response.data.success) {
        toast.success(response.data.message || t('users.students.updateSuccess'), { duration: 3000, position: 'top-center' });
        closeEditModal();
        fetchStudents(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error updating student:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || t('users.students.updateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsUpdating(false);
    }
  };

  // Update student level
  const updateStudentLevel = async () => {
    if (!updatingStudentLevel) return;

    // Validation
    const errors = {};
    if (!levelFormData.newLevelId) {
      errors.newLevelId = t('users.students.newLevelRequired');
    }
    if (!levelFormData.graduationDate?.trim()) {
      errors.graduationDate = t('users.students.graduationDateRequired');
    }

    setLevelFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsUpdatingLevel(true);
    try {
      const requestData = {
        newLevelId: parseInt(levelFormData.newLevelId),
        graduationDate: levelFormData.graduationDate
      };

      console.log('Update Student Level Request Data:', requestData);
      console.log('API Endpoint: /students/' + updatingStudentLevel.id + '/level');

      const response = await api.put(`/students/${updatingStudentLevel.id}/level`, requestData);

      if (response.data.success) {
        toast.success(response.data.message || t('users.students.levelUpdateSuccess'), { duration: 3000, position: 'top-center' });
        closeLevelModal();
        fetchStudents(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error updating student level:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || t('users.students.levelUpdateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsUpdatingLevel(false);
    }
  };

  // Delete student
  const deleteStudent = async (studentId, studentName) => {
    if (window.confirm(t('users.students.confirmDelete', { name: studentName }))) {
      try {
        console.log('Delete Student Request - Student ID:', studentId);
        console.log('API Endpoint: /students/' + studentId);

        const response = await api.delete(`/students/${studentId}`);

        if (response.data.success) {
          toast.success(response.data.message || t('users.students.deleteSuccess'), { duration: 3000, position: 'top-center' });
          // Refresh the students list
          fetchStudents(currentPage, searchTerm, appliedFilters);
        }
      } catch (error) {
        console.error('Error deleting student:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        
        const errorMessage = error.response?.data?.message || error.message || t('users.students.deleteError');
        toast.error(errorMessage, { duration: 3000, position: 'top-center' });
      }
    }
  };


  // Toggle student status
  const toggleStudentStatus = async (studentId, currentStatus) => {
    try {
      const response = await api.put(`/students/${studentId}/toggle-status`, {
        isActive: !currentStatus
      });
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.students.statusUpdated'), { duration: 3000, position: 'top-center' });
        fetchStudents(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error toggling student status:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.students.statusUpdateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    }
  };

  // Initial load
  useEffect(() => {
    setFilters({ isActive: '', sortBy: 'first_name', sortOrder: 'asc' });
    setAppliedFilters({ isActive: '', sortBy: 'first_name', sortOrder: 'asc', learningLevelId: '', departmentId: '' });
    setSearchTerm('');
    setCurrentPage(1);
    fetchLearningLevels();
    fetchDepartmentsForFilter();
    fetchStudents(1, '', { isActive: '', sortBy: 'first_name', sortOrder: 'asc' });
  }, []);

  // Re-translate learning levels when language changes
  useEffect(() => {
    if (learningLevels.length > 0) {
      const updatedLevels = learningLevels.map(level => ({
        ...level,
        translatedName: learningLevelsData[level.name] ? 
          learningLevelsData[level.name][currentLanguage] || level.name : 
          level.name
      }));
      setLearningLevels(updatedLevels);
    }
  }, [currentLanguage]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary-100 rounded-lg">
                <GraduationCapIcon className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('users.students.title')}
              </h1>
            </div>
            <p className="text-gray-600">
              {currentLevelInfo 
                ? `${t('users.students.filteredBy')} ${translateLevelName(currentLevelInfo.name)}` 
                : t('users.students.description')
              }
            </p>
          </div>

          {/* Bulk actions summary + button (top-right, similar to Users page) */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {t('users.students.bulkSelectedCount', { count: selectedStudentIds.length })}
            </span>
            <button
              type="button"
              onClick={openBulkLevelModal}
              disabled={selectedStudentIds.length === 0}
              title={t('users.students.bulkUpdateLevel')}
              className={`p-2 rounded-lg transition-colors ${
                selectedStudentIds.length === 0
                  ? 'bg-purple-100 text-purple-300 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <GraduationCapIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          {/* الصف الأول: البحث + الحالة + المستوى */}
          <div className={`flex flex-col md:flex-row gap-4 mb-4 w-full ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            {/* Search */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('common.search')}
              </label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input
                  type="text"
                  placeholder={t('users.students.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50/50 hover:bg-white`}
                />
                {isSearching && (
                  <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
                  </div>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.students.status')}
              </label>
              <select
                value={filters.isActive}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilters(prev => ({ ...prev, isActive: val }));
                  const newFilters = { ...filters, isActive: val };
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="">{t('users.students.allStatuses')}</option>
                <option value="true">{t('users.students.active')}</option>
                <option value="false">{t('users.students.inactive')}</option>
              </select>
            </div>

            {/* Learning Level Filter */}
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.students.level')}
              </label>
              <select
                value={filters.learningLevelId}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, learningLevelId: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="">{t('users.students.allLevels')}</option>
                {learningLevels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.translatedName}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* الصف الثاني: القسم + الترتيب + عدد العناصر + أزرار الأكشن */}
          <div className={`flex flex-col md:flex-row gap-4 w-full ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            {/* Department Filter */}
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.students.department')}
              </label>
              <select
                value={filters.departmentId}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, departmentId: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                disabled={departmentsLoading}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('users.students.allDepartments')}</option>
                {filterDepartments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {getDepartmentTranslation(dept.name, currentLanguage)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By - auto-applies */}
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.students.sortBy')}
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, sortBy: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="first_name">{t('users.students.sortByFirstName')}</option>
                <option value="last_name">{t('users.students.sortByLastName')}</option>
                <option value="student_number">{t('users.students.sortByStudentNumber')}</option>
                <option value="enrollment_date">{t('users.students.sortByEnrollmentDate')}</option>
              </select>
            </div>

            {/* Sort Order - auto-applies */}
            <div className="md:flex-1 md:max-w-[9rem]">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.students.sortOrder')}
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, sortOrder: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="asc">{t('users.students.sortAsc')}</option>
                <option value="desc">{t('users.students.sortDesc')}</option>
              </select>
            </div>

            {/* Items per page */}
            <div className="md:flex-1 md:max-w-[9rem]">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.perPage')}
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                {LIMIT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2 md:flex-shrink-0">
              <button
                type="submit"
                className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                {t('users.students.applyFilters')}
              </button>
              {(getActiveFiltersCount() > 0 || searchTerm) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  title={t('common.clearFilters')}
                  className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={students.length > 0 && selectedStudentIds.length === students.length}
                    onChange={toggleSelectAllStudents}
                  />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.name')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.contact')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.studentInfo')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.enrollmentDate')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.status')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.students.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="ml-2">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    {t('users.students.noStudents')}
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    {/* Selection */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedStudentIds.includes(student.id)}
                        onChange={() => toggleSelectStudent(student.id)}
                      />
                    </td>
                    {/* Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className={`ml-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
                          <div className="text-sm font-medium text-gray-900">
                            {student.first_name} {student.last_name}
                          </div>
                          <div className="text-sm text-gray-500">@{student.username}</div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Contact - Email & Phone */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-1">
                        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                          <MailIcon className={`h-3 w-3 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span>{student.email}</span>
                        </div>
                        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                          <PhoneIcon className={`h-3 w-3 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span>{student.phone || t('common.notAvailable')}</span>
                        </div>
                      </div>
                    </td>
                    
                    {/* Student Info - Student Number & Level */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-2">
                        <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {student.student_number}
                          </span>
                        </div>
                        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                          <ShieldIcon className={`h-4 w-4 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span>{translateLevelName(student.level_name)}</span>
                        </div>
                      </div>
                    </td>
                    
                    {/* Enrollment Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.enrollment_date ? new Date(student.enrollment_date).toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US') : t('common.notAvailable')}
                    </td>
                    
                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleStudentStatus(student.id, student.is_active)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          student.is_active 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {student.is_active ? (
                          <>
                            <UserCheckIcon className="h-3 w-3 mr-1" />
                            {t('users.students.active')}
                          </>
                        ) : (
                          <>
                            <UserXIcon className="h-3 w-3 mr-1" />
                            {t('users.students.inactive')}
                          </>
                        )}
                      </button>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className={`flex ${isRTL ? 'justify-end space-x-reverse space-x-1' : 'justify-start space-x-1'}`}>
                        {/* View Profile */}
                        <button
                          onClick={() => viewStudentProfile(student.id)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                          title={t('users.students.viewProfile')}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>

                        {/* Statistics */}
                        <button
                          onClick={() => navigate(`/dashboard/students/${student.user_id ?? student.id}/statistics`)}
                          className="text-amber-600 hover:text-amber-900 p-1 rounded transition-colors"
                          title={t('users.students.statistics')}
                        >
                          <BarChart3Icon className="h-4 w-4" />
                        </button>

                        {/* Edit Student */}
                        <button
                          onClick={() => openEditModal(student)}
                          className="text-green-600 hover:text-green-900 p-1 rounded transition-colors"
                          title={t('users.students.edit')}
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>

                        {/* Update Student Level */}
                        <button
                          onClick={() => openLevelModal(student)}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors"
                          title={t('users.students.updateLevel')}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </button>

                        {/* Delete Student */}
                        <button
                          onClick={() => deleteStudent(student.id, `${student.first_name} ${student.last_name}`)}
                          className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                          title={t('users.students.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          {/* Pagination info and controls */}
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
          </div>
          
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t('common.showing')} <span className="font-medium">{totalItems > 0 ? ((currentPage - 1) * limit) + 1 : 0}</span> {t('common.to')} <span className="font-medium">{Math.min(currentPage * limit, totalItems)}</span> {t('common.of')} <span className="font-medium">{totalItems}</span> {t('common.results')}
              </p>
            </div>
            
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  {/* Previous button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    // First page + ellipsis
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                    }

                    // Visible pages
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            i === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Last page + ellipsis
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}

                  {/* Next button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Student Modal */}
      {showEditModal && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {t('users.students.editStudent')}
              </h2>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); updateStudent(); }} className="space-y-4">
              {/* Student Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.studentNumber')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingStudent.studentNumber}
                  onChange={(e) => setEditingStudent(prev => ({ ...prev, studentNumber: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.studentNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('users.students.studentNumberPlaceholder')}
                />
                {editFormErrors.studentNumber && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.studentNumber}</p>
                )}
              </div>

              {/* Enrollment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.enrollmentDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editingStudent.enrollmentDate}
                  onChange={(e) => setEditingStudent(prev => ({ ...prev, enrollmentDate: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.enrollmentDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.enrollmentDate && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.enrollmentDate}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={isUpdating}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  disabled={isUpdating}
                >
                  {isUpdating ? t('common.updating') : t('common.update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Student Level Modal */}
      {showLevelModal && updatingStudentLevel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {t('users.students.updateLevel')}
              </h2>
              <button
                onClick={closeLevelModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>{t('users.students.student')}:</strong> {updatingStudentLevel.first_name} {updatingStudentLevel.last_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>{t('users.students.currentLevel')}:</strong> {translateLevelName(updatingStudentLevel.level_name)}
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); updateStudentLevel(); }} className="space-y-4">
              {/* New Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.newLevel')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={levelFormData.newLevelId}
                  onChange={(e) => setLevelFormData(prev => ({ ...prev, newLevelId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    levelFormErrors.newLevelId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('users.students.selectLevel')}</option>
                  {learningLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.translatedName}
                    </option>
                  ))}
                </select>
                {levelFormErrors.newLevelId && (
                  <p className="mt-1 text-sm text-red-600">{levelFormErrors.newLevelId}</p>
                )}
              </div>

              {/* Graduation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.graduationDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={levelFormData.graduationDate}
                  onChange={(e) => setLevelFormData(prev => ({ ...prev, graduationDate: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    levelFormErrors.graduationDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {levelFormErrors.graduationDate && (
                  <p className="mt-1 text-sm text-red-600">{levelFormErrors.graduationDate}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeLevelModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={isUpdatingLevel}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  disabled={isUpdatingLevel}
                >
                  {isUpdatingLevel ? t('common.updating') : t('users.students.updateLevel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Update Student Level Modal */}
      {showBulkLevelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {t('users.students.bulkUpdateLevel')}
              </h2>
              <button
                onClick={closeBulkLevelModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUpdatingBulkLevel}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {t('users.students.bulkSelectedCount', { count: selectedStudentIds.length })}
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateStudentsLevelBulk();
              }}
              className="space-y-4"
            >
              {/* New Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.newLevel')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={bulkLevelFormData.newLevelId}
                  onChange={(e) =>
                    setBulkLevelFormData((prev) => ({ ...prev, newLevelId: e.target.value }))
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    bulkLevelFormErrors.newLevelId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('users.students.selectLevel')}</option>
                  {learningLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.translatedName}
                    </option>
                  ))}
                </select>
                {bulkLevelFormErrors.newLevelId && (
                  <p className="mt-1 text-sm text-red-600">{bulkLevelFormErrors.newLevelId}</p>
                )}
              </div>

              {/* Graduation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.students.graduationDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={bulkLevelFormData.graduationDate}
                  onChange={(e) =>
                    setBulkLevelFormData((prev) => ({
                      ...prev,
                      graduationDate: e.target.value
                    }))
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    bulkLevelFormErrors.graduationDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {bulkLevelFormErrors.graduationDate && (
                  <p className="mt-1 text-sm text-red-600">{bulkLevelFormErrors.graduationDate}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeBulkLevelModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={isUpdatingBulkLevel}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  disabled={isUpdatingBulkLevel}
                >
                  {isUpdatingBulkLevel ? t('common.updating') : t('users.students.updateLevel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
